import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useMutation } from 'hooks/sympl-mutation';
import { FormProvider, useForm } from 'react-hook-form';
import 'twin.macro';
import ConfigForm, { ConfigFormQuestionType } from './ConfigForm';
import SaveButton from 'components/save-button/SaveButton';
import AppPage from 'components/page/app-page/AppPage';
import { useQuery } from 'hooks/sympl-query';
import useNavigationContext from 'hooks/context/nav-context';
import { TargetingLocation } from 'types/geolocationTypes';
import { GET_TARGETING_CONFIG } from 'graphql/vacancies/queries';
import { UPDATE_TARGETING_CONFIG } from 'graphql/vacancies/mutations';
import useGetStartedContext from 'hooks/context/get-started-context';
import MissingFields from 'components/page/app-page/MissingFields';
import { JobType } from 'types/targetingTypes';
import { GET_JOBTYPES_SUBJOBTYPES } from 'graphql/jobtypes/queries';
import { getOpenAiThreadsResponse } from 'utils/openAiHelpers';
import Button from 'components/button/Button';
import { Routes } from 'types/routeTypes';
import { unset } from 'lodash';
import { ToastTypes } from 'types/notificationTypes';
import { useToastNotifications } from 'hooks/notificationHooks';
import { useNavigate } from 'react-router-dom';
import SaveStatus from 'components/save-status/SaveStatus';
import UndoRedo from 'components/undo-redo/UndoRedo';
import { useAutoSave } from 'hooks/useAutoSave';
import { useUndoRedo } from 'hooks/useUndoRedo';
import { usePendingSave } from 'context/PendingSaveContext';
import { sendKeepaliveSave } from 'utils/keepaliveSave';
import { SCREEN_NAME, nextSendNo } from 'utils/screenName';
import { WORK_WAS_KEPT } from 'api/waitingChanges';
import OutOfDateBox from 'components/out-of-date/OutOfDateBox';
import {
  TargetingWork,
  missingForTargeting,
  notSavedBecause,
  TargetingIsPublished,
  saveTargeting,
  storedLocations,
  targetingPath,
  targetingPayload,
} from 'api/targetingAutoSave';

const PUBLISHED_MEANWHILE_KEY = 'sympl:targeting-published-meanwhile';
/** Claude's wording, not yet approved by Steven. */
export const PUBLISHED_MEANWHILE_NOTICE =
  'This campaign was published while you had it open. The screen was loaded again.';

/** Stands for "the screen is not complete, there is nothing to store yet". */
type NothingToStoreYet = { incomplete: true };
const NOTHING_TO_STORE_YET: NothingToStoreYet = { incomplete: true };

const valueOf = (data: TargetingFormData[], key: ConfigFormQuestionKey) =>
  data.flatMap(({ questions }) => questions).find((q) => q.key === key)?.value;

/** The screen's fields as one plain value, copied so a later edit cannot reach it. */
const workOf = (data: TargetingFormData[]): TargetingWork =>
  JSON.parse(
    JSON.stringify({
      vac_name: valueOf(data, 'vac_name')?.toString() ?? '',
      brand: Number(valueOf(data, 'brand')) || null,
      job_type: Number(valueOf(data, 'job_type')) || null,
      sub_job_type: Number(valueOf(data, 'sub_job_type')) || null,
      language: Number(valueOf(data, 'language')) || null,
      experience: (valueOf(data, 'experience') as string[]) ?? [],
      location: (valueOf(data, 'location') as TargetingLocation[]) ?? [],
    })
  );

export type ConfigFormQuestionKey =
  | 'experience'
  | 'function_name'
  | 'job_type'
  | 'sub_job_type'
  | 'vac_name'
  | 'brand'
  | 'language'
  | 'location'
  | 'radius';

export interface TargetingFormData {
  section: string;
  questions: {
    key: ConfigFormQuestionKey;
    label: string;
    helper?: string;
    type: ConfigFormQuestionType;
    value?: string | number | TargetingLocation[] | string[] | null;
  }[];
}

export interface TargetingConfiguration {
  targetingConfig: {
    published: boolean;
    targeting: {
      job_type?: number;
      sub_job_type?: number | null;
      experience?: string[];
      locations?: TargetingLocation[];
      language_id?: number;
      vac_name?: string;
      brand_id?: number | null;
      /** When the server last stored this screen. */
      kept_at?: string | null;
      /** The job is open: changes here wait for "Publish changes". */
      waits?: boolean;
      /** D10: the count of targeting this answer was read at. */
      kept_version?: number | null;
    };
  };
}

interface TargetingPayload {
  vacancyId: number;
  input: {
    locations: TargetingLocation[];
    can_exp: string[];
    can_range: number;
    job_type: number;
    sub_job_type: number | null;
    language_id: number;
    vac_name: string;
    brand_id: number | null;
  };
}

const TargetingConfig: React.FC = () => {
  const navigate = useNavigate();
  // AppPage temporarily unmounts the inputs while campaign data is refreshing.
  const formMethods = useForm({ shouldUnregister: false });
  const { reset } = formMethods;
  const { nextItem, refetchCheckList } = useGetStartedContext();

  const {
    activeVacancy,
    currentVacancy,
    hasUnsavedChanges,
    setHasUnsavedChanges,
    brands,
    refetchCurrentVacancy,
  } = useNavigationContext();
  const { addToast } = useToastNotifications();
  const { setPending, registerAbandon, registerSaveNow, switching } =
    usePendingSave();

  const [formKey, setFormKey] = useState(0);
  const [vacancyIsPublished, setVacancyIsPublished] = useState(false);
  /**
   * The campaign's job is open: this screen stores itself and what it stores
   * waits for "Publish changes" (slice 5). A published campaign that is not
   * open (paused) keeps its Save button.
   */
  const [waitsForPublish, setWaitsForPublish] = useState(false);
  const [formData, setFormData] = useState<TargetingFormData[]>();
  const [isDoneUpdating, setIsDoneUpdating] = useState(false);
  const [suggestedJobType, setSuggestedJobType] = useState<JobType>();
  const [suggestedSubJobType, setSuggestedSubJobType] = useState<JobType>();
  /**
   * The campaign the form on screen was built FOR. Picking another campaign
   * does not rebuild this screen, so for a moment the form still holds the
   * previous campaign's targeting; auto-save and undo must not take that for
   * the open campaign's.
   */
  const [builtFor, setBuiltFor] = useState<number | null>(null);
  const builtForRef = useRef<number | null>(null);
  /**
   * The server has been asked again since the campaign changed. Until then
   * the answer in hand is the previous campaign's.
   */
  const askedAfresh = useRef(false);
  /** The locations as the server holds them, and for which campaign. */
  const stored = useRef<{
    of: number | null;
    locations: TargetingLocation[];
    /** False once a save may have changed them without the screen knowing. */
    sure: boolean;
  }>({ of: null, locations: [], sure: false });
  /**
   * The campaign was published while this screen was open (a colleague,
   * another tab): the server refused the screen's own store. From then on
   * the screen has its Save button, like any published campaign.
   */
  const [publishedMeanwhile, setPublishedMeanwhile] = useState<number | null>(
    null
  );
  /**
   * D10: the count of targeting this screen is built on. It arrives with the
   * screen and moves with every answer of the server to a save of ours.
   */
  const builtOn = useRef(new Map<number, number>());
  /** "Keep mine": the next save is marked as overwriting. */
  const overwriteOnce = useRef(false);
  /** The caret is in the function-title box. */
  const [titleFocused, setTitleFocused] = useState(false);

  // Loaded again after "published meanwhile": say why, once.
  useEffect(() => {
    let was = false;
    try {
      was = sessionStorage.getItem(PUBLISHED_MEANWHILE_KEY) === '1';
      sessionStorage.removeItem(PUBLISHED_MEANWHILE_KEY);
    } catch (_e) {
      // No storage: the screen is still right, only without the notice.
    }
    if (was) {
      addToast({
        type: ToastTypes.INFO,
        description: PUBLISHED_MEANWHILE_NOTICE,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const watchVac = formMethods.watch('vac_name');
  const watchBrand = formMethods.watch('brand');
  const watchJobType = formMethods.watch('job_type');
  const watchSubJobType = formMethods.watch('sub_job_type');
  const watchLocation = formMethods.watch('location');
  const missingFields = useMemo(
    () => [
      ...(!watchVac ? ['No function title provided'] : []),
      ...(!watchBrand ? ['No name provided'] : []),
      ...(!watchJobType ? ['No function type provided'] : []),
      ...(!(watchSubJobType || watchJobType === 18)
        ? ['No function subtype provided']
        : []),
      ...(!watchLocation || !(watchLocation as TargetingLocation[]).length
        ? ['No locations provided']
        : []),
    ],
    [watchBrand, watchJobType, watchLocation, watchSubJobType, watchVac]
  );

  const {
    loading: loadingConfig,
    data: configData,
    refetch,
  } = useQuery<TargetingConfiguration, { vacancyId: number }>(
    GET_TARGETING_CONFIG,
    {
      skip: !activeVacancy,
      fetchPolicy: 'network-only',
      notifyOnNetworkStatusChange: true,
      variables: { vacancyId: activeVacancy ?? 0 },
    }
  );

  const [
    updateConfig,
    { loading: updateConfigLoading, error: updateConfigError },
  ] = useMutation<undefined, TargetingPayload>(UPDATE_TARGETING_CONFIG);

  /**
   * Updates the form field value using a given section & question index.
   * @param sectionIndex The form section index
   * @param questionIndex The form question index
   * @param key The form question key
   * @param value The updated value
   */
  const formDataChangeHandler = (
    sectionIndex: number,
    questionIndex: number,
    _key: ConfigFormQuestionKey,
    value: string | number | TargetingLocation[] | string[] | null
  ) => {
    const data = formData ? [...formData] : [];
    if (!data?.[sectionIndex]?.questions?.[questionIndex]) return;
    data[sectionIndex].questions[questionIndex].value = value;
    setFormData(data);
    // A campaign that keeps itself has no Save button to light up.
    if (!autoKeepsRef.current) setHasUnsavedChanges(true);
  };

  /**
   * Gets a question value using it's corresponding key
   * @param key The question key
   * @returns The question values
   */
  const getConfigValueByKey = (key: ConfigFormQuestionKey) => {
    return formData
      ?.flatMap(({ questions }) => questions)
      ?.find((q) => q.key === key)?.value;
  };

  const submitForm = async () => {
    let isValid = await formMethods.trigger();
    // Sub job type not required when 'other' category is chosen
    if (
      formMethods.getValues('job_type') !== 18 &&
      !formMethods.getValues('sub_job_type')
    ) {
      formMethods.setError('sub_job_type', { type: 'required' });
      isValid = false;
    }
    if (loadingConfig || !isValid || !activeVacancy) return Promise.reject();

    // Parse the candidate location
    const locations = getConfigValueByKey('location') as
      | TargetingLocation[]
      | undefined;

    if (!locations) return Promise.reject();

    // if the locations' id matches the id of one or the default locations, remove the id
    const brandDefaultLocations = brands?.find(
      (b) => b.id === getConfigValueByKey('brand')
    )?.default_locations;

    locations.forEach((location) => {
      if (brandDefaultLocations?.some((l) => l.id === location.id)) {
        unset(location, 'id');
      }
    });

    const brand = getConfigValueByKey('brand') as string | number;

    // Construct the mutation payload
    const input: TargetingPayload['input'] = {
      locations: locations?.map((location) => {
        return { ...location, lon: location.lng };
      }) as any,
      can_exp: (getConfigValueByKey('experience') as string[]) ?? ['exp_none'],
      can_range: (getConfigValueByKey('radius') as number) ?? 16,
      job_type: (getConfigValueByKey('job_type') as number) ?? 0,
      sub_job_type: (getConfigValueByKey('sub_job_type') as number) ?? null,
      language_id: (getConfigValueByKey('language') as number) ?? 141,
      vac_name: getConfigValueByKey('vac_name')?.toString() ?? '',
      brand_id: (brand as number) ?? null,
    };

    await updateConfig({
      variables: {
        vacancyId: activeVacancy,
        input,
      },
    });

    addToast({
      type: ToastTypes.SUCCESS,
      description: 'Your targeting settings have been successfully updated',
    });

    refetch();
    refetchCurrentVacancy();

    refetchCheckList();
    setHasUnsavedChanges(false);
    return Promise.resolve();
  };

  /*
   * Auto-save for targeting (slice 3): a campaign that was never published
   * keeps this screen by itself, like the job page. A published campaign
   * keeps its Save button for now: there a location change goes to the ad
   * platforms at once, and that has to wait for "Publish changes" first.
   */
  const own = !!activeVacancy && builtFor === activeVacancy;
  // Since Steven's answer of 4 Oct 2026 the screen stores itself on EVERY
  // campaign: never published and closed ones straight away, a running one
  // kept until "Publish changes". The Save button below is no longer reached.
  const autoKeeps = own && publishedMeanwhile !== activeVacancy;
  const autoKeepsRef = useRef(autoKeeps);
  autoKeepsRef.current = autoKeeps;

  const work = useMemo(
    () => (own && formData ? workOf(formData) : undefined),
    [own, formData]
  );

  /*
   * The function title joins what is stored only when the caret LEAVES its
   * box (D36: "once, when the person leaves the title field"). Every stored
   * title change makes the server replace eight job-page texts with template
   * text, hand-written ones included. Stored two seconds into a pause, that
   * happened mid-word, and again for a title typed and then put back (review
   * of slice 3).
   */
  const leftTitle = useRef<{ of: number; title: string } | null>(null);
  if (work && activeVacancy) {
    if (leftTitle.current?.of !== activeVacancy || !titleFocused) {
      leftTitle.current = { of: activeVacancy, title: work.vac_name };
    }
  }
  const toStore = useMemo(
    () =>
      work && titleFocused && leftTitle.current
        ? { ...work, vac_name: leftTitle.current.title }
        : work,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [work, titleFocused]
  );
  const missing = useMemo(
    () => (toStore ? missingForTargeting(toStore) : []),
    [toStore]
  );

  /*
   * An incomplete screen is NOT stored: the server refuses it whole, and it
   * deletes every stored location a request leaves out. What auto-save sees
   * while the screen is incomplete is the last complete state, so nothing is
   * sent; the corner says why, and leaving asks.
   */
  const lastComplete = useRef<{ of: number; work: TargetingWork } | null>(null);
  const opened = useRef<{ of: number; print: string } | null>(null);
  if (toStore && activeVacancy) {
    if (opened.current?.of !== activeVacancy) {
      opened.current = { of: activeVacancy, print: JSON.stringify(toStore) };
      lastComplete.current = null;
    }
    if (!missing.length)
      lastComplete.current = { of: activeVacancy, work: toStore };
  }
  const storable: TargetingWork | NothingToStoreYet | undefined = !toStore
    ? undefined
    : !missing.length
    ? toStore
    : lastComplete.current?.work ?? NOTHING_TO_STORE_YET;
  const heldBack =
    autoKeeps &&
    !!toStore &&
    missing.length > 0 &&
    JSON.stringify(toStore) !==
      (lastComplete.current
        ? JSON.stringify(lastComplete.current.work)
        : opened.current?.print);
  const heldBackRef = useRef(heldBack);
  heldBackRef.current = heldBack;

  const bodyFor = (campaign: number, value: TargetingWork) => ({
    ...targetingPayload(
      value,
      stored.current.of === campaign ? stored.current.locations : []
    ),
    // Tells the server this is the screen storing ITSELF: it refuses that on
    // a campaign that has been published meanwhile.
    kept_by_itself: true,
    // D10: which open screen this is, and the count it is built on.
    screen: SCREEN_NAME,
    sent_no: nextSendNo(),
    ...(builtOn.current.has(campaign)
      ? { based_on: builtOn.current.get(campaign) }
      : {}),
  });

  const storeTargeting = useCallback(
    async (
      value: TargetingWork | NothingToStoreYet,
      signal?: AbortSignal
    ): Promise<string> => {
      if (!activeVacancy) throw new Error('No campaign is open.');
      if ('incomplete' in value) return new Date().toISOString();
      const campaign = activeVacancy;
      const stillOpen = () => openCampaign.current === campaign;

      /*
       * The numbers of the locations must be the server's, as of NOW. After a
       * save that failed, or whose answer never came, the list in hand may be
       * out of date, and the server refuses a number it no longer has: every
       * retry would then be refused for ever (review of slice 3).
       */
      if (stored.current.of !== campaign || !stored.current.sure) {
        const locations = await storedLocations(campaign, signal);
        if (stillOpen())
          stored.current = { of: campaign, locations, sure: true };
      }

      const body = {
        ...bodyFor(campaign, value),
        ...(overwriteOnce.current ? { overwrite: true } : {}),
      };
      if (stillOpen()) stored.current = { ...stored.current, sure: false };
      try {
        const keptVersion = await saveTargeting(campaign, body, signal);
        overwriteOnce.current = false;
        if (keptVersion !== null) builtOn.current.set(campaign, keptVersion);
      } catch (error) {
        if (!(error instanceof TargetingIsPublished)) throw error;
        /*
         * Not a failure to retry. The campaign was published while this
         * screen was open, so the screen is no longer what the server would
         * hand out: fields that are greyed out on a published campaign are
         * still open here. It is loaded afresh (the effect below), and says
         * why when it is back.
         */
        setPublishedMeanwhile(campaign);
        return new Date().toISOString();
      }

      // The work IS stored. Reading the new numbers back is a convenience:
      // if it fails, the next save reads them first.
      try {
        const locations = await storedLocations(campaign, signal);
        if (stillOpen())
          stored.current = { of: campaign, locations, sure: true };
      } catch (_e) {
        // left as "not sure"
      }
      return new Date().toISOString();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeVacancy]
  );

  const sendOnTheWayOut = (
    campaign: number,
    value: TargetingWork | NothingToStoreYet
  ) => {
    if ('incomplete' in value) return;
    sendKeepaliveSave(targetingPath(campaign), bodyFor(campaign, value));
  };

  const {
    state: saveState,
    keptAt,
    hasPending,
    hasFailed,
    abandon,
    saveNow,
    isKept,
    refusal,
    retryRefused,
  } = useAutoSave<TargetingWork | NothingToStoreYet>({
    value: autoKeeps ? storable : undefined,
    enabled: autoKeeps,
    // Not the bare number: the job page counts its own sends under that.
    identity: activeVacancy ? `targeting:${activeVacancy}` : null,
    // 5b item 3: opening the screen already says when it was last stored.
    keptAtOnOpen: own ? configData?.targetingConfig?.targeting?.kept_at : null,
    save: storeTargeting,
    flushOnHide: (value) => {
      if (activeVacancy) sendOnTheWayOut(activeVacancy, value);
    },
    flushOnIdentityChange: (previous, value) => {
      const campaign = Number(String(previous).replace('targeting:', ''));
      if (campaign) sendOnTheWayOut(campaign, value);
    },
  });

  /** One undo step puts a whole earlier screen back, and rebuilds the form. */
  const putBack = (earlier: TargetingWork) => {
    setFormData((current) =>
      current?.map((section) => ({
        ...section,
        questions: section.questions.map((question) => ({
          ...question,
          value:
            question.key in earlier
              ? (earlier[question.key as keyof TargetingWork] as
                  | string
                  | number
                  | TargetingLocation[]
                  | string[]
                  | null)
              : question.value,
        })),
      }))
    );
    Object.entries(earlier).forEach(([key, value]) =>
      formMethods.setValue(key, value)
    );
    // The form is rebuilt: the caret is in no box any more, and a box that is
    // taken away does not say so.
    setTitleFocused(false);
    setFormKey(Math.random());
  };

  const { canUndo, canRedo, undo, redo } = useUndoRedo<TargetingWork>({
    value: autoKeeps ? work : undefined,
    apply: putBack,
    enabled: autoKeeps,
    identity: activeVacancy,
  });

  // Published meanwhile: give up what cannot be stored and load afresh.
  useEffect(() => {
    if (!activeVacancy || publishedMeanwhile !== activeVacancy) return;
    try {
      sessionStorage.setItem(PUBLISHED_MEANWHILE_KEY, '1');
    } catch (_e) {
      // see above
    }
    abandon();
    const reload = window.setTimeout(() => window.location.reload(), 100);
    return () => window.clearTimeout(reload);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publishedMeanwhile, activeVacancy]);

  /*
   * What the sidebar and the campaign picker ask before taking the person
   * away. A screen held back for being incomplete answers "not kept", so the
   * same question is asked as for a save that failed (scene 12); whatever CAN
   * be stored is stored first.
   */
  useEffect(() => {
    if (!autoKeeps) return undefined;
    setPending((hasPending && hasFailed) || heldBack);
    registerAbandon(() => {
      overwriteOnce.current = false;
      abandon();
    });
    registerSaveNow(
      () => (heldBackRef.current ? saveNow().then(() => false) : saveNow()),
      () => !heldBackRef.current && isKept()
    );
    return () => {
      setPending(false);
      registerAbandon(null);
      registerSaveNow(null);
    };
  }, [
    autoKeeps,
    hasPending,
    hasFailed,
    heldBack,
    setPending,
    registerAbandon,
    abandon,
    registerSaveNow,
    saveNow,
    isKept,
  ]);

  /*
   * What used to follow the Save button now follows a save that landed: the
   * sidebar's checklist is asked again. NOT the campaign itself: while that
   * is read again the page shows its loading blocks instead of the form, so
   * the form was taken away and rebuilt after every save and whatever was
   * typed in that moment was dropped (review of slice 3).
   */
  useEffect(() => {
    if (!autoKeeps || saveState !== 'saved' || hasPending) return;
    refetchCheckList();
    // On a running campaign what was just kept is not public yet: the
    // sidebar's "changes not yet public" follows it (scene 6).
    window.dispatchEvent(new CustomEvent(WORK_WAS_KEPT));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveState, hasPending]);

  const { data: jobsAndSubjobTypes } = useQuery<{ jobTypes: JobType[] }, {}>(
    GET_JOBTYPES_SUBJOBTYPES
  );

  const activeBrand = useMemo(
    () =>
      brands?.find(
        (b) => b.id === configData?.targetingConfig?.targeting?.brand_id
      ) ?? null,
    [brands, configData?.targetingConfig?.targeting]
  );

  const updateWithExternalData = useCallback(async () => {
    const config = configData?.targetingConfig?.targeting;

    /*
     * A campaign that keeps itself is built ONCE per opening. Nothing that
     * arrives later for the same campaign may rebuild the form, not even the
     * query merely looking again: it would take the caret out of the field
     * being typed in and put older values over newer ones.
     */
    if (
      !!activeVacancy &&
      builtForRef.current === activeVacancy &&
      autoKeepsRef.current
    ) {
      return;
    }

    // A campaign with a Save button being read again after a Save: the form
    // stays as it is until the new answer is there.
    if (
      loadingConfig &&
      !!activeVacancy &&
      builtForRef.current === activeVacancy
    ) {
      return;
    }

    const isOfThisCampaign =
      !!config && !loadingConfig && !!activeVacancy && askedAfresh.current;
    builtForRef.current = isOfThisCampaign ? activeVacancy ?? null : null;
    setBuiltFor(builtForRef.current);
    if (isOfThisCampaign) {
      stored.current = {
        of: activeVacancy ?? null,
        locations: config?.locations ?? [],
        sure: true,
      };
      if (activeVacancy && config?.kept_version != null) {
        builtOn.current.set(activeVacancy, config.kept_version);
      }
    }

    setVacancyIsPublished(!!configData?.targetingConfig?.published);
    setWaitsForPublish(!!configData?.targetingConfig?.targeting?.waits);

    const data: TargetingFormData[] = [
      {
        section: 'What',
        questions: [
          {
            key: 'vac_name',
            label: 'What is the function title?',
            helper: 'This is the function title candidates will see.',
            type: 'vac-name',
            value: config?.vac_name ?? '',
          },
          {
            key: 'brand',
            label: "What is your company's brand name?",
            type: 'brand',
            value: config?.brand_id ?? null,
          },
          // The next two need to be adjacent to each other.
          // job_type needs to be followed directly by sub_job_type!
          {
            key: 'job_type',
            label: 'What is the function type?',
            helper: 'Choose the function type that is closest to your vacancy',
            type: 'job-type',
            value: config?.job_type ?? null,
          },
          {
            key: 'sub_job_type',
            label: '',
            type: 'sub-job-type',
            value: config?.sub_job_type ?? null,
          },
        ],
      },
      {
        section: 'Who',
        questions: [
          {
            key: 'language',
            label: "What's the language of this campaign?",
            helper:
              'This will be used for your ads, vacancy page & application form',
            type: 'language',
            value:
              config?.language_id ?? activeBrand?.default_language_id ?? '',
          },
          {
            key: 'experience',
            label: 'How many years of experience are ideal for this function?',
            type: 'experience',
            value: config?.experience ?? ['exp_none'],
          },
        ],
      },
      {
        section: 'Where',
        questions: [
          {
            key: 'location',
            label: 'Where do you want to look for candidates?',
            helper: 'Provide one or more towns, cities or countries.',
            type: 'geolocation',
            value: config?.locations ?? [],
          },
        ],
      },
    ];

    // Keep validation defaults in sync when the form remounts after a save/refetch.
    reset(
      Object.fromEntries(
        data.flatMap(({ questions }) =>
          questions.map(({ key, value }) => [key, value ?? ''])
        )
      )
    );
    setFormData(data);
    setIsDoneUpdating(true);
    setFormKey(Math.random());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    reset,
    configData?.targetingConfig?.published,
    configData?.targetingConfig?.targeting,
    loadingConfig,
    activeVacancy,
  ]);

  // Another campaign: whatever is on screen now is the previous one's.
  const openCampaign = useRef(activeVacancy);
  if (openCampaign.current !== activeVacancy) {
    openCampaign.current = activeVacancy;
    askedAfresh.current = false;
    builtForRef.current = null;
    // A "Keep mine" that never went through does not travel along.
    overwriteOnce.current = false;
  }
  if (loadingConfig) askedAfresh.current = true;

  useEffect(() => {
    updateWithExternalData();
  }, [configData?.targetingConfig?.targeting, updateWithExternalData]);

  useEffect(() => {
    const config = configData?.targetingConfig?.targeting;
    (async () => {
      // If there is no job type, use AI to pre-fill it
      if (config && !config.job_type) {
        try {
          const subJobTypeResponse = await getOpenAiThreadsResponse({
            vacancy_id: activeVacancy!,
            identifier: 'vac_targeting_jobtype',
            messages: [
              {
                role: 'user',
                content: `Identify the single most relevant category & subJob type for the function title '${config.vac_name}'`,
              },
            ],
          });

          const parsedResponse = JSON.parse(subJobTypeResponse.response);

          // traverse through Job types returned from the backend and look for the subJob that matches the description
          jobsAndSubjobTypes?.jobTypes.forEach((jobType: JobType) => {
            const foundSubJobType = jobType.sub_job_types.find(
              ({ name }) =>
                name.toLowerCase() === parsedResponse.subJob?.toLowerCase()
            );

            if (foundSubJobType) {
              setSuggestedJobType(jobType);
              setSuggestedSubJobType(foundSubJobType as JobType);
            }
          });
        } catch (error) {
          // Best-effort pre-fill: on failure the user picks the job type by
          // hand. Re-throwing only made an unhandled rejection (SYM-6753).
          console.warn('AI job type suggestion failed', error);
        }
      }
    })();
  }, [configData?.targetingConfig?.targeting, jobsAndSubjobTypes?.jobTypes]);

  return (
    <AppPage
      heading="Targeting"
      loading={!isDoneUpdating}
      skeleton={
        <div
          tw="h-full grid grid-cols-2 grid-rows-2 gap-4 mt-8"
          style={{
            gridTemplateAreas: '"what who" "where where"',
          }}
        >
          <div
            tw="bg-gray-200 h-full w-full animate-pulse rounded-md"
            css={{
              gridArea: 'what',
            }}
          />
          <div
            tw="bg-gray-200 h-full w-full animate-pulse rounded-md"
            css={{
              gridArea: 'who',
            }}
          />
          <div
            tw="bg-gray-200 h-full w-full animate-pulse rounded-md"
            css={{
              gridArea: 'where',
            }}
          />
        </div>
      }
      cta={
        <div tw="flex space-x-5">
          {!!missingFields.length && (
            <MissingFields missingFields={missingFields} />
          )}
          {(autoKeeps ? true : !hasUnsavedChanges) &&
          nextItem &&
          nextItem?.key !== 'targeting' ? (
            <Button
              testId="targeting-next-step-button"
              variant="indigo"
              /*
               * While a store is waiting, or a field is missing, the button
               * cannot be used but KEEPS ITS PLACE. It used to disappear for
               * those two seconds, and Undo, Redo and "Saved at" slid over to
               * where it had been (Steven's hand test of 5 Oct 2026: a second
               * click on Undo landed on the wrong spot).
               */
              disabled={autoKeeps && (hasPending || !!missing.length)}
              onClick={() =>
                navigate(
                  nextItem.key === 'advertising'
                    ? Routes.AD_EDITOR
                    : Routes.JOB_POSTING
                )
              }
            >
              Go to next step &rarr;
            </Button>
          ) : (
            !autoKeeps &&
            own && (
              <SaveButton
                shouldSave={hasUnsavedChanges}
                loading={updateConfigLoading}
                error={updateConfigError}
                onClick={submitForm}
              />
            )
          )}
          {autoKeeps && (
            <>
              <UndoRedo
                canUndo={canUndo}
                canRedo={canRedo}
                onUndo={undo}
                onRedo={redo}
              />
              <SaveStatus
                state={saveState}
                keptAt={keptAt}
                switching={switching}
                notSavedBecause={
                  heldBack ? notSavedBecause(missing) : undefined
                }
              />
            </>
          )}
        </div>
      }
    >
      <OutOfDateBox
        refusal={autoKeeps ? refusal : null}
        onLoadTheirs={() => {
          // The screen is loaded afresh, so the undo steps go with it (D12).
          overwriteOnce.current = false;
          abandon();
          window.setTimeout(() => window.location.reload(), 100);
        }}
        onKeepMine={() => {
          overwriteOnce.current = true;
          retryRefused();
        }}
      />
      {/* The browser checks' proof that the form on screen is THIS campaign's. */}
      {own && (
        <span
          hidden
          data-testid="targeting-content-of"
          data-campaign={activeVacancy ?? ''}
        />
      )}
      <div
        tw="contents"
        onFocusCapture={(event) => {
          if ((event.target as HTMLElement).id === 'vac_name') {
            setTitleFocused(true);
          }
        }}
        onBlurCapture={(event) => {
          if ((event.target as HTMLElement).id === 'vac_name') {
            setTitleFocused(false);
          }
        }}
      >
        <FormProvider {...formMethods}>
          <ConfigForm
            formData={formData}
            channels={currentVacancy?.channels ?? []}
            key={formKey}
            isPublished={vacancyIsPublished}
            onChange={formDataChangeHandler}
            suggestedJobType={suggestedJobType}
            suggestedSubJobType={suggestedSubJobType}
          />
        </FormProvider>
      </div>
    </AppPage>
  );
};

export default TargetingConfig;
